import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Space, Modal, Form, Input, Select, message, Tag, Popconfirm, Switch, Typography, Row, Col, Tooltip, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined, CodeOutlined, InfoCircleOutlined, CloudOutlined, FileTextOutlined } from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const platformOptions = [
    { value: 'centos', label: 'CentOS', color: 'blue' },
    { value: 'ubuntu', label: 'Ubuntu', color: 'orange' },
    { value: 'debian', label: 'Debian', color: 'red' },
    { value: 'linux', label: 'Linux (Other)', color: 'green' },
    { value: 'windows', label: 'Windows', color: 'cyan' },
];

const typeOptions = [
    { value: 'init', label: '初始化脚本', icon: <FileTextOutlined />, description: '虚拟机首次启动时执行的脚本' },
    { value: 'sethostname', label: '设置主机名', icon: <CodeOutlined />, description: '用于设置虚拟机主机名的脚本' },
    { value: 'setip', label: '设置IP', icon: <CodeOutlined />, description: '用于配置虚拟机网络的脚本' },
    { value: 'cloudinit', label: 'CloudInit配置', icon: <CloudOutlined />, description: 'CloudInit YAML配置文件' },
];

const StartupScriptManagement = () => {
    const [loading, setLoading] = useState(false);
    const [scripts, setScripts] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
    const [modalVisible, setModalVisible] = useState(false);
    const [editingScript, setEditingScript] = useState(null);
    const [filters, setFilters] = useState({ keyword: '', platform: '', type: '', status: '' });
    const [form] = Form.useForm();
    const [selectedType, setSelectedType] = useState('init');

    useEffect(() => {
        fetchScripts();
    }, [pagination.current, filters]);

    const fetchScripts = async () => {
        setLoading(true);
        try {
            const response = await evmAdminService.getStartupScriptList({
                page: pagination.current,
                per_page: pagination.pageSize,
                ...filters,
            });
            if (response.status === 'success') {
                setScripts(response.data.data || []);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.total || 0,
                }));
            }
        } catch (error) {
            message.error('获取脚本列表失败');
        }
        setLoading(false);
    };

    const handleCreate = () => {
        setEditingScript(null);
        form.resetFields();
        form.setFieldsValue({
            platform: 'linux',
            type: 'init',
            status: 'enable',
            content: '',
        });
        setSelectedType('init');
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingScript(record);
        form.setFieldsValue(record);
        setSelectedType(record.type);
        setModalVisible(true);
    };

    const handleSubmit = async (values) => {
        try {
            let response;
            if (editingScript) {
                response = await evmAdminService.updateStartupScript(editingScript.id, values);
            } else {
                response = await evmAdminService.createStartupScript(values);
            }
            if (response.status === 'success') {
                message.success(editingScript ? '脚本更新成功' : '脚本创建成功');
                setModalVisible(false);
                fetchScripts();
            } else {
                message.error(response.msg || '操作失败');
            }
        } catch (error) {
            message.error('操作失败');
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await evmAdminService.deleteStartupScript(id);
            if (response.status === 'success') {
                message.success('脚本删除成功');
                fetchScripts();
            } else {
                message.error(response.msg || '删除失败');
            }
        } catch (error) {
            message.error('删除失败');
        }
    };

    const handleToggleStatus = async (id) => {
        try {
            const response = await evmAdminService.toggleStartupScriptStatus(id);
            if (response.status === 'success') {
                message.success('状态更新成功');
                fetchScripts();
            } else {
                message.error(response.msg || '状态更新失败');
            }
        } catch (error) {
            message.error('状态更新失败');
        }
    };

    const getTypeInfo = (type) => {
        return typeOptions.find(t => t.value === type) || { label: type, icon: <CodeOutlined /> };
    };

    const getPlatformInfo = (platform) => {
        return platformOptions.find(p => p.value === platform) || { label: platform, color: 'default' };
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
        },
        {
            title: '名称',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    {getTypeInfo(record.type).icon}
                    <span>{text}</span>
                </Space>
            ),
        },
        {
            title: '平台',
            dataIndex: 'platform',
            key: 'platform',
            width: 120,
            render: (platform) => {
                const info = getPlatformInfo(platform);
                return <Tag color={info.color}>{info.label}</Tag>;
            },
        },
        {
            title: '类型',
            dataIndex: 'type',
            key: 'type',
            width: 140,
            render: (type) => {
                const info = getTypeInfo(type);
                return (
                    <Tooltip title={info.description}>
                        <Tag icon={info.icon}>{info.label}</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status, record) => (
                <Switch
                    checked={status === 'enable'}
                    onChange={() => handleToggleStatus(record.id)}
                    checkedChildren="启用"
                    unCheckedChildren="禁用"
                />
            ),
        },
        {
            title: '更新时间',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 180,
        },
        {
            title: '操作',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除这个脚本吗？"
                        description="如果有镜像在使用此脚本，将无法删除"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // CloudInit 示例模板
    const getExampleContent = (type) => {
        switch (type) {
            case 'cloudinit':
                return `#cloud-config
# CloudInit 配置示例
hostname: \${HOSTNAME}
manage_etc_hosts: true

users:
  - name: root
    lock_passwd: false

chpasswd:
  list: |
    root:\${PASSWORD}
  expire: False

# 网络配置
network:
  version: 2
  ethernets:
    eth0:
      addresses:
        - \${IP_ADDRESS}/\${NETMASK}
      gateway4: \${GATEWAY}
      nameservers:
        addresses:
          - 8.8.8.8
          - 1.1.1.1

# 首次启动执行的命令
runcmd:
  - systemctl restart sshd
`;
            case 'init':
                return `#!/bin/bash
# 初始化脚本示例
# 可用变量: \${HOSTNAME}, \${PASSWORD}, \${IP_ADDRESS}, \${NETMASK}, \${GATEWAY}

echo "Setting hostname to \${HOSTNAME}..."
hostnamectl set-hostname \${HOSTNAME}

echo "Initialization complete."
`;
            case 'sethostname':
                return `#!/bin/bash
# 设置主机名脚本
hostnamectl set-hostname \${HOSTNAME}
echo "\${IP_ADDRESS} \${HOSTNAME}" >> /etc/hosts
`;
            case 'setip':
                return `#!/bin/bash
# 设置IP脚本
# 根据操作系统版本选择相应的网络配置方式

# CentOS/RHEL 7+
cat > /etc/sysconfig/network-scripts/ifcfg-eth0 <<EOF
TYPE=Ethernet
BOOTPROTO=static
NAME=eth0
DEVICE=eth0
ONBOOT=yes
IPADDR=\${IP_ADDRESS}
NETMASK=\${NETMASK}
GATEWAY=\${GATEWAY}
DNS1=8.8.8.8
DNS2=1.1.1.1
EOF

systemctl restart network
`;
            default:
                return '';
        }
    };

    return (
        <div>
            <Card>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <Title level={4} style={{ margin: 0 }}>
                            <CloudOutlined style={{ marginRight: 8 }} />
                            启动脚本管理 (CloudInit)
                        </Title>
                    </Col>
                    <Col>
                        <Space>
                            <Button icon={<ReloadOutlined />} onClick={fetchScripts}>
                                刷新
                            </Button>
                            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                                新建脚本
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Alert
                    message="启动脚本说明"
                    description="启动脚本用于虚拟机初始化配置，支持传统Shell脚本和CloudInit配置。可以在镜像管理中关联启动脚本。"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                {/* 筛选区 */}
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={6}>
                        <Input
                            placeholder="搜索名称"
                            prefix={<SearchOutlined />}
                            value={filters.keyword}
                            onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                            onPressEnter={fetchScripts}
                        />
                    </Col>
                    <Col span={4}>
                        <Select
                            placeholder="平台"
                            value={filters.platform || undefined}
                            onChange={(value) => setFilters({ ...filters, platform: value })}
                            allowClear
                            style={{ width: '100%' }}
                        >
                            {platformOptions.map(p => (
                                <Option key={p.value} value={p.value}>{p.label}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={4}>
                        <Select
                            placeholder="类型"
                            value={filters.type || undefined}
                            onChange={(value) => setFilters({ ...filters, type: value })}
                            allowClear
                            style={{ width: '100%' }}
                        >
                            {typeOptions.map(t => (
                                <Option key={t.value} value={t.value}>{t.label}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={4}>
                        <Select
                            placeholder="状态"
                            value={filters.status || undefined}
                            onChange={(value) => setFilters({ ...filters, status: value })}
                            allowClear
                            style={{ width: '100%' }}
                        >
                            <Option value="enable">启用</Option>
                            <Option value="disable">禁用</Option>
                        </Select>
                    </Col>
                    <Col span={2}>
                        <Button icon={<SearchOutlined />} onClick={fetchScripts}>搜索</Button>
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={scripts}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条`,
                        onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize }),
                    }}
                />
            </Card>

            {/* 创建/编辑模态框 */}
            <Modal
                title={editingScript ? '编辑启动脚本' : '新建启动脚本'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={() => form.submit()}
                width={800}
                destroyOnClose
            >
                <Form form={form} onFinish={handleSubmit} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="脚本名称"
                                rules={[{ required: true, message: '请输入脚本名称' }]}
                            >
                                <Input placeholder="输入脚本名称" />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="platform"
                                label="适用平台"
                                rules={[{ required: true, message: '请选择平台' }]}
                            >
                                <Select>
                                    {platformOptions.map(p => (
                                        <Option key={p.value} value={p.value}>{p.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="status"
                                label="状态"
                            >
                                <Select>
                                    <Option value="enable">启用</Option>
                                    <Option value="disable">禁用</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="type"
                        label={
                            <Space>
                                脚本类型
                                <Tooltip title="CloudInit配置适用于支持cloud-init的镜像">
                                    <InfoCircleOutlined />
                                </Tooltip>
                            </Space>
                        }
                        rules={[{ required: true, message: '请选择类型' }]}
                    >
                        <Select onChange={(value) => setSelectedType(value)}>
                            {typeOptions.map(t => (
                                <Option key={t.value} value={t.value}>
                                    <Space>
                                        {t.icon}
                                        {t.label}
                                        <span style={{ color: '#888', fontSize: 12 }}>- {t.description}</span>
                                    </Space>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="content"
                        label={
                            <Space>
                                脚本内容
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => {
                                        const example = getExampleContent(selectedType);
                                        form.setFieldsValue({ content: example });
                                    }}
                                >
                                    插入示例
                                </Button>
                            </Space>
                        }
                    >
                        <TextArea
                            rows={16}
                            placeholder={`输入${selectedType === 'cloudinit' ? 'CloudInit YAML配置' : 'Shell脚本'}内容...`}
                            style={{ fontFamily: 'monospace' }}
                        />
                    </Form.Item>

                    <Alert
                        message="可用变量"
                        description={
                            <span>
                                脚本中可以使用以下变量（系统会自动替换）：<br />
                                <code>${'${HOSTNAME}'}</code> - 主机名，
                                <code>${'${PASSWORD}'}</code> - root密码，
                                <code>${'${IP_ADDRESS}'}</code> - IP地址，
                                <code>${'${NETMASK}'}</code> - 子网掩码，
                                <code>${'${GATEWAY}'}</code> - 网关
                            </span>
                        }
                        type="info"
                        showIcon
                    />
                </Form>
            </Modal>
        </div>
    );
};

export default StartupScriptManagement;
